import { memo } from "react";
import { Button } from "@/components/ui/button";
import {
  Check,
  ChevronRight,
  Loader2,
  X,
  AlertCircle,
  Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatRelative } from "date-fns";
import type { AgentSdkMessage } from "@forgepoint/shared";
import { Permission } from "@forgepoint/shared";
import {
  type RichActionType,
  getRichActionDisplayName,
  getRichActionSubtitle,
} from "@/modules/agents/components/helpers/richActionHelpers";
import { useAuthStore, useHasPermission } from "@/store/authStore";
import { canUserApproveTask } from "@/lib/auth-utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface RichActionToolCallCardProps {
  message: AgentSdkMessage;
  richActionType: RichActionType;
  onOpenRichActionPanel?: (messageId: string) => void;
  richActionPanelOpen: boolean;
  onApprove: (taskId: string) => void;
  onDeny: (taskId: string) => void;
  isApproving: boolean;
  isDenying: boolean;
  /** Error message when the action failed to execute. */
  error?: string | null;
}

export const RichActionToolCallCard = memo(function RichActionToolCallCard({
  message,
  richActionType,
  onOpenRichActionPanel,
  richActionPanelOpen,
  onApprove,
  onDeny,
  isApproving,
  isDenying,
  error,
}: RichActionToolCallCardProps) {
  const user = useAuthStore((state) => state.user);
  const canApproveAllTasks = useHasPermission(Permission.TASKS_MANAGE);
  const canApprove = canUserApproveTask(user, message.task, canApproveAllTasks);
  const canOpen = onOpenRichActionPanel !== undefined;

  const updatedAt = message.createdAt;
  const isFailed = !!error;
  const isRequiresReview = message.task?.status === "PENDING_REVIEW";
  const isCancelled = message.task?.status === "CANCELLED";
  const isCompleted = message.task?.status === "APPROVED";
  const showStatusLabel =
    isFailed || isRequiresReview || isCancelled || isCompleted;
  const statusLabelPrefix = isFailed
    ? "Failed "
    : isCancelled
      ? "Cancelled "
      : isCompleted
        ? "Completed "
        : isRequiresReview
          ? "Pending since "
          : "";

  if (isFailed) {
    return (
      <div className="pl-11">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between border rounded-lg p-3 cursor-default border-danger/50 bg-danger/5 opacity-60">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-danger shrink-0" />
              <div>
                <div className="font-medium text-sm">
                  {getRichActionDisplayName(richActionType)}
                </div>
                <div className="text-xs text-muted-foreground">
                  Failed to execute
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <span className="text-xs text-muted-foreground">
                {statusLabelPrefix}
                {formatRelative(updatedAt, new Date())}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const waitingForLabel = getWaitingForLabel(message.task);
  const cannotApproveTooltip =
    !canApprove && isRequiresReview
      ? `Only assigned reviewers${waitingForLabel ? ` (${waitingForLabel})` : ""} can approve this action`
      : null;

  const cardContent = (
    <div className="flex flex-col gap-2">
      <div
        className={cn(
          "group flex flex-col gap-2",
          "border rounded-lg p-3 transition-all duration-150",
          canOpen
            ? "cursor-pointer hover:bg-muted/50 hover:shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            : "cursor-default",
          isRequiresReview && "border-warning/50 bg-warning/5",
          isCancelled && "opacity-50",
          isCompleted && "border-success/50 bg-success/5",
        )}
        role={canOpen ? "button" : undefined}
        tabIndex={canOpen ? 0 : undefined}
        onClick={() => onOpenRichActionPanel?.(message.id)}
        onKeyDown={(event) => {
          if (
            onOpenRichActionPanel &&
            (event.key === "Enter" || event.key === " ")
          ) {
            event.preventDefault();
            onOpenRichActionPanel(message.id);
          }
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div>
              <div className="font-medium text-sm">
                {getRichActionDisplayName(richActionType)}
              </div>
              <div className="text-xs text-muted-foreground">
                {getRichActionSubtitle(
                  richActionType,
                  message.message.type === "tool_call"
                    ? message.message.arguments
                    : {},
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 justify-end">
            {showStatusLabel && (
              <span className="text-xs text-muted-foreground group-hover:opacity-100 transition-opacity opacity-0">
                {statusLabelPrefix}
                {formatRelative(updatedAt, new Date())}
              </span>
            )}

            {canOpen ? (
              richActionPanelOpen ? (
                <X className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )
            ) : null}
          </div>
        </div>
        {isRequiresReview && !canApprove && waitingForLabel && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3 shrink-0" />
            <span>Waiting for {waitingForLabel}</span>
          </div>
        )}
      </div>
      {isRequiresReview && message.task && canApprove && (
        <div className="flex gap-2">
          <Button
            className="bg-green-600 hover:bg-green-700 text-white gap-1.5"
            onClick={() => onApprove(message.task!.id)}
            disabled={isApproving || isDenying}
          >
            {isApproving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Check className="h-3.5 w-3.5" />
                Approve
              </>
            )}
          </Button>
          <Button
            variant="outline"
            className="gap-1.5"
            onClick={() => onDeny(message.task!.id)}
            disabled={isApproving || isDenying}
          >
            {isDenying ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <X className="h-3.5 w-3.5" />
                Cancel
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="pl-11">
      {cannotApproveTooltip ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{cardContent}</TooltipTrigger>
            <TooltipContent>
              <p>{cannotApproveTooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        cardContent
      )}
    </div>
  );
});

/**
 * Builds a display label for who the task is waiting on from the task's assignees.
 * @param task - The task with optional assignees
 * @returns A comma-separated string of assignee names, or null if no assignees
 */
function getWaitingForLabel(task: AgentSdkMessage["task"]): string | null {
  if (!task?.assignees?.length) {
    return null;
  }

  const names = task.assignees
    .map((a) => {
      if (a.firstName || a.lastName) {
        return `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim();
      }
      return a.email ?? null;
    })
    .filter((name): name is string => name !== null);

  if (names.length === 0) {
    return null;
  }

  return names.join(", ");
}
